/**
 * The score breakdown's insights (Lj, Oct 11: "the score screen … should be a deeper dive … more about the DATA, how it
 * affects the score, and what we need to work on"). Built on deptgoals.js — the score is the mean of the goals' pace
 * marks, so each goal with a mark is worth 100 ÷ n points:
 *
 *   scoreWaterfall   0 → the score: each goal's points earned (+) and lost (−), integers that sum to the score exactly
 *   goalLine         one plain-English line per goal ("Efficiency is 12 points behind: 3 tasks ran over — …")
 *   scoreMoves       the moves that would raise the score most, each found by re-scoring a copy of the data with the
 *                    move done (finish a task on its estimate, put the hours in, log the photos, close the bugs…)
 *   scoreDays        the score as it stood at the end of each of the last 7 / 30 days (the day scrubber)
 *   goalDaily        one goal's value each day of its period, with the pace line (the goal sheet's sparkline)
 *   dataHealth       what limits accuracy here: finished tasks with no estimate or no time, jobs with no contract value
 *                    or bill rate, photos with no time, untagged finished work, receipts on no job, goals with no target
 *
 * Pure — no I/O.
 */
import {
  deptGoalsView, goalWindow, goalValue, goalMetric, goalsFor, workedDates, formatGoalValue, looseReceiptCount, fixedPresets,
  suggestedGoals, deptRole, goalDaysText, STATUS_LABEL, goalWindowFor,
} from './deptgoals.js';
import { taskGoalMoves, dueDateText } from './taskgoals.js';   // a task goal behind pace: work N h on it, finish N subtasks (B6)
import { workDepartments } from './workdept.js';
import { departmentOf } from './hierarchy.js';
import { contractValueOf } from './profit.js';
import { earnedByJobType } from './jobpay.js';
import { taskPhotoList } from './taskphotos.js';
import { isOverheadTask } from './deptscore.js';
import { projectStatus } from './projectstatus.js';
import { localDateStr } from './time.js';
import { addDaysStr } from './phonestats.js';
import { canonTag } from './tags.js';

const H = 3600000;
const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const hrs = (ms) => { const h = ms / H; return h < 1 ? `${Math.round(ms / 60000)} min` : `${h >= 10 ? Math.round(h) : Math.round(h * 10) / 10} h`; };
const doneDate = (t) => (t?.status === 'done' && t.completedAt ? localDateStr(t.completedAt) : null);
const hasTag = (t, tag) => Array.isArray(t?.tags) && t.tags.some((x) => canonTag(String(x)) === tag);
const isOpen = (t) => t && !t.deletedAt && t.status !== 'done' && t.status !== 'skipped';
const listNames = (xs, max = 3) => (xs.length <= max ? xs.join(', ') : `${xs.slice(0, max).join(', ')} +${xs.length - max} more`);

/** The score unrounded: the mean of the marks (what view.score rounds). null when no goal has a mark. */
export function exactScore(view) {
  const m = (view?.goals || []).filter((g) => g.mark != null);
  return m.length ? m.reduce((a, g) => a + g.mark, 0) / m.length : null;
}

/** Integers that sum to `total`, each as close to its share as can be (largest remainder; ties to the earlier one). */
function apportion(shares, total) {
  const floors = shares.map((x) => Math.floor(x + 1e-9));
  let left = total - floors.reduce((a, b) => a + b, 0);
  const order = shares.map((x, i) => ({ i, r: x - floors[i] })).sort((a, b) => b.r - a.r || a.i - b.i);
  for (let k = 0; left > 0 && k < order.length; k += 1, left -= 1) floors[order[k].i] += 1;
  return floors;
}

/**
 * The score from 0 up: each goal that has a mark is one step — `pts` earned (its mark × its weight) and `lost` (the
 * rest of its share). Steps run carrying-first (fewest lost), so the drags sit next to the total. Points are integers
 * that add up to the score (Σ pts = score, Σ lost = 100 − score); `exact` keeps the unrounded shares.
 * Goals with no mark (no target, waiting on a feed, nothing measured, a day off) are listed in `out` with why.
 * → { n, weight, steps: [{ goal, pts, lost, start, end, exactPts, exactLost }], score, exact, out: [{ goal, why }] }
 */
export function scoreWaterfall(view) {
  const goals = view?.goals || [];
  const marked = goals.filter((g) => g.mark != null);
  const out = goals.filter((g) => g.mark == null).map((g) => ({ goal: g, why: notScoredWhy(g) }));
  const n = marked.length;
  if (!n) return { n: 0, weight: null, steps: [], score: null, exact: null, out };
  const weight = 100 / n;
  const exact = exactScore(view);
  const score = view.score ?? Math.round(exact);
  const pts = apportion(marked.map((g) => (g.mark * weight) / 100), score);
  const lost = apportion(marked.map((g) => ((100 - g.mark) * weight) / 100), 100 - score);
  const rows = marked.map((g, i) => ({ goal: g, pts: pts[i], lost: lost[i], exactPts: (g.mark * weight) / 100, exactLost: ((100 - g.mark) * weight) / 100 }))
    .sort((a, b) => a.exactLost - b.exactLost || b.exactPts - a.exactPts);
  let at = 0;
  const steps = rows.map((r) => { const s = { ...r, start: at, end: at + r.pts }; at += r.pts; return s; });
  return { n, weight, steps, score, exact, out };
}

/** Why a goal isn't in the score, in words. */
export function notScoredWhy(g) {
  if (g.status === 'off') return g.when === 'clocked' ? 'not counted today — it counts on days you work here' : `not counted today — it counts ${goalDaysText(g.days)}`;
  if (g.status === 'unset') return g.needsRate ? 'not in the score — set your rate in BattleBooks' : 'not in the score — it has no target yet';
  if (g.status === 'feed') return `not in the score — waiting on ${g.feed === 'sales' ? 'the Bids feed' : 'the books'}`;
  return `not in the score — nothing to measure yet${g.notes?.length ? ` (${g.notes[0]})` : ''}`;
}

/**
 * One goal in plain English: how many points it's behind (or that it carries its share), then why, from the rows its
 * count kept. `step` = its scoreWaterfall step (null when it isn't scored). → { text, lead, why, tone }
 */
export function goalLine(g, step, data = {}, { past = false } = {}) {
  if (!step) return { lead: g.label, why: notScoredWhy(g), text: `${g.label}: ${notScoredWhy(g)}.`, tone: 'none' };
  const f = (v) => formatGoalValue(v, g.unit);
  const lead = step.lost > 0 ? `${g.label} is ${plural(step.lost, 'point')} behind` : `${g.label} carries its full ${step.pts}`;
  let why = '';
  const tasks = data.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  if (g.key === 'onEstimate' && g.value != null) {
    const over = (g.rows || []).map((r) => byId.get(r.taskId)).filter(Boolean)
      .map((t) => ({ t, over: (num(t.timeSpent) || 0) - (num(t.estimatedMinutes) || 0) * 60000 })).filter((x) => x.over >= 6 * 60000)
      .sort((a, b) => b.over - a.over);
    why = over.length
      ? `${plural(over.length, 'task')} ran over — ${listNames(over.map((x) => `${x.t.title} (+${hrs(x.over)})`), 2)}`
      : `${f(g.value)} — ${g.basis || 'on the estimates'}`;
  } else if (g.key === 'productiveShare' && g.value != null) {
    const other = (g.rows || []).filter((r) => r.group === 'other').map((r) => `${r.label} ${String(r.title).split(' · ').pop()}`);
    why = other.length ? `${f(g.value)} of your hours were this department’s work; the rest went to ${listNames(other, 3)}` : `${f(g.value)} — ${g.basis}`;
  } else if (g.key.startsWith('task:') && g.target != null) {   // a task goal: its measure, against the pace to its period's end
    const per = g.period === 'until' ? `by ${dueDateText(g.due)}` : `this ${g.period}`;
    if (g.status === 'met' || g.status === 'under') why = `${(g.measureLabel || '').toLowerCase()} ${f(g.value)} of ${f(g.target)} — met`;
    else if (g.kind === 'count' && g.direction === 'up') why = `${(g.measureLabel || '').toLowerCase()} ${f(g.value)}, ${f(Math.round((g.expected || 0) * 10) / 10)} expected by ${past ? 'then' : 'now'} (${f(g.target)} ${per})`;
    else why = `${(g.measureLabel || '').toLowerCase()} ${f(g.value)} against a limit of ${f(g.target)}`;
  } else if (g.kind === 'count' && g.direction === 'up' && g.target != null) {
    if (g.status === 'met') why = `met — ${f(g.value)} of ${f(g.target)}`;
    else why = `${f(g.value)} done, ${f(Math.round((g.expected || 0) * 10) / 10)} expected by ${past ? 'then' : 'now'} (${f(g.target)} ${past ? 'that' : 'this'} ${g.period})`;
  } else if (g.direction === 'down' && g.target != null) {
    if (g.value > g.target) {
      const oldest = g.key.startsWith('open:') && g.rows?.[0] ? ` — oldest: ${g.rows[0].title}${g.rows[0].ageDays != null ? ` (${g.rows[0].ageDays} d)` : ''}` : '';
      why = `${f(g.value)} against a limit of ${f(g.target)}${oldest}`;
    } else why = `${f(g.value)}, inside the limit of ${f(g.target)}`;
    if (g.value > g.target && !g.key.startsWith('open:') && g.rows?.length) why += ` — ${listNames(g.rows.map((r) => r.title || r.label).filter(Boolean), 2)}`;
  } else if (g.target != null) {
    why = `${f(g.value)} against ${f(g.target)}${g.basis ? ` — ${g.basis}` : ''}`;
  }
  if (step.lost > 0 && g.notes?.length && g.key !== 'productiveShare' && !g.key.startsWith('open:')) why += `; ${g.notes[0]}`;   // open:'s note is about how age is read, not what was left out
  return { lead, why, text: why ? `${lead}: ${why}.` : `${lead}.`, tone: step.lost === 0 ? 'met' : g.status };
}

// ── the day scrubber ────────────────────────────────────────────────────────────────────────────────────────────

/** The moment a day is read at: now for today, else 11:59 pm that day (counts and rates as they stood then). */
export const dayMoment = (date, now = Date.now()) => (date >= localDateStr(now) ? now : new Date(`${date}T23:59:00`).getTime());

/** The view as it stood at the end of `date` (today = now). */
export function viewOn(dept, data = {}, date, { now = data.now || Date.now() } = {}) {
  const at = dayMoment(date, now);
  return deptGoalsView(dept, { ...data, now: at }, { now: at });
}

/**
 * The last `days` days (oldest first, today last) with the score at the end of each — the scrubber's ticks. Levels
 * (open bugs, overdue AR…) have no history, so they read as they stand on every day.
 * → [{ date, score, view }]
 */
export function scoreDays(dept, data = {}, { now = data.now || Date.now(), days = 7 } = {}) {
  const today = localDateStr(now);
  const out = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = addDaysStr(today, -i);
    const view = viewOn(dept, data, date, { now });
    out.push({ date, score: view.score, view });
  }
  return out;
}

/**
 * One goal each day: for a week / month goal, its period so far (Monday or the 1st → today), the value as it stood
 * that night and where pace said it should be (count = target × the share gone; rate / level = the target); for a
 * day goal, the last `days` days each on its own. Levels keep no history → []. → [{ date, value, expected }]
 */
export function goalDaily(goal, dept, data = {}, { now = data.now || Date.now(), days = 7 } = {}) {
  const metric = goalMetric(goal.key, { metrics: data.metrics || [], personalMetrics: data.personalMetrics || [], goal, tasks: data.tasks });
  if (!metric || metric.kind === 'level') return [];
  const today = localDateStr(now);
  const worked = goal.when === 'clocked' ? workedDates(dept, { ...data, now }) : null;
  const target = num(goal.target);
  const dates = [];
  if (goal.period === 'day') for (let i = days - 1; i >= 0; i -= 1) dates.push(addDaysStr(today, -i));
  else for (let d = goalWindowFor(goal, now).from; d <= today; d = addDaysStr(d, 1)) dates.push(d);
  return dates.map((date) => {
    const at = dayMoment(date, now);
    const w = goalWindowFor(goal, at, 0, worked);
    if (w.off) return { date, value: null, expected: null, off: true };
    const v = goalValue({ ...goal }, dept, w, { ...data, now: at }).value;
    const expected = target == null ? null : metric.kind === 'count' && (goal.direction || metric.direction) === 'up' ? Math.round(target * w.share * 10) / 10 : target;
    return { date, value: v, expected };
  });
}

// ── what to work on ─────────────────────────────────────────────────────────────────────────────────────────────

/** The department's open work, best first: the running task, overdue, today, scheduled, then the rest (oldest first). */
export function deptOpenTasks(dept, data = {}, { activeTaskId = null, date = localDateStr(data.now || Date.now()) } = {}) {
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const hasOpenKids = new Set(tasks.filter((t) => isOpen(t) && t.parentId).map((t) => t.parentId));
  const rank = (t) => (t.id === activeTaskId ? 0 : t.scheduledDate && t.scheduledDate < date ? 1 : t.scheduledDate === date ? 2 : t.scheduledDate ? 3 : 4);
  return tasks.filter((t) => isOpen(t) && !t.bucket && !t.quickTaskId && !t.runId && !hasOpenKids.has(t.id) && wd.forTask(t)?.id === dept?.id)
    .sort((a, b) => rank(a) - rank(b) || String(a.scheduledDate || '').localeCompare(String(b.scheduledDate || '')) || (num(a.createdAt) || 0) - (num(b.createdAt) || 0));
}

const replaceTasks = (data, changed) => {
  const map = new Map(changed.map((t) => [t.id, t]));
  return { ...data, tasks: (data.tasks || []).map((t) => map.get(t.id) || t) };
};
/** The task finished now, on its estimate: the rest of the estimate (if any) is worked today first. */
const spentOn = (t) => Math.max(num(t.timeSpent) || 0, (t.timeEntries || []).reduce((a, e) => a + (num(e?.duration) || 0), 0));   // the live entry counts
function finished(t, now) {
  const est = (num(t.estimatedMinutes) || 0) * 60000;
  const spent = spentOn(t);
  const remain = est > 0 ? Math.max(0, est - spent) : 0;
  const timeEntries = remain > 0 ? [...(t.timeEntries || []), { id: `sim-${t.id}`, date: localDateStr(now), duration: remain, createdAt: now }] : (t.timeEntries || []);
  return { ...t, status: 'done', completedAt: now, timeSpent: spent + remain, timeEntries };
}
const closed = (t, now) => ({ ...t, status: 'done', completedAt: now });
const withHours = (t, ms, now) => ({ ...t, timeSpent: (num(t.timeSpent) || 0) + ms, timeEntries: [...(t.timeEntries || []), { id: `sim-h-${t.id}`, date: localDateStr(now), duration: ms, createdAt: now }] });
const withPhotos = (t, k, now) => ({ ...t, shots: [...(Array.isArray(t.shots) ? t.shots : []), ...Array.from({ length: k }, (_, i) => ({ uri: `sim://${t.id}/${i}`, tag: 'progress', at: now, takenAt: now }))] });

/**
 * The moves that would raise the score most, ranked by what they'd add (Lj, Oct 11: "what we need to work on"). Each
 * is checked, not guessed: the data is copied with the move done and the department re-scored. Kinds:
 *   finish     finish one open task here on its estimate (its remaining estimate worked today)
 *   finishN    finish the next N tasks (a task count / #tag count behind pace)
 *   hours      put the hours a time goal is behind into today (on the running task, else the next one)
 *   photos     log the photos a photo goal is behind (on the running task, else the next one)
 *   log        log a counter a counter goal is behind on (+1 at a tap)
 *   close      close the oldest tasks over a limit (open #bug, waiting to deploy, open tasks, the oldest task)
 *   receipts   put receipts on a job (Books current / receipts waiting)
 * → [{ id, kind, title, why, gain (points, rounded), exactGain, starts (no score yet: gain = where it would start), goals: [labels], action: { kind, taskId?, metricId?, personal?, goalId? } }]
 */
export function scoreMoves(view, dept, data = {}, { now = data.now || Date.now(), activeTaskId = null, limit = 6, candidates = 6 } = {}) {
  if (!view || !dept) return [];
  const base = exactScore(view);
  const ctxNow = { ...data, now };
  const rescore = (sim) => deptGoalsView(dept, sim, { now });
  const open = deptOpenTasks(dept, ctxNow, { activeTaskId });
  const lead = open[0] || null;
  const moves = [];
  const add = (m, sim) => {
    const v = rescore(sim);
    const after = exactScore(v);
    if (after == null) return;
    // no score yet: the move starts one — ranked by where it would start (`starts`)
    const exactGain = base == null ? after : after - base;
    if (exactGain < 0.5) return;
    const lifted = v.goals.filter((g) => { const was = view.goals.find((x) => x.id === g.id); return g.mark != null && (was?.mark == null || g.mark > was.mark); }).map((g) => g.label);
    moves.push({ ...m, gain: Math.round(exactGain), exactGain, starts: base == null, goals: lifted, seq: moves.length });
  };

  // one task at a time — the next few open tasks here
  for (const t of open.slice(0, candidates)) {
    const est = (num(t.estimatedMinutes) || 0) * 60000; const left = Math.max(0, est - spentOn(t));
    add({ id: `finish:${t.id}`, kind: 'finish', title: `Finish ${t.title}`, why: est ? (left > 0 ? `about ${hrs(left)} left on its ${hrs(est)} estimate` : `already past its ${hrs(est)} estimate`) : 'no estimate — counts as one more done', action: { kind: t.id === activeTaskId ? 'open' : 'start', taskId: t.id } },
    replaceTasks(ctxNow, [finished(t, now)]));
  }

  for (const g of view.goals) {
    if (g.mark == null || g.mark >= 100 || g.target == null) continue;
    if (g.key.startsWith('task:')) { for (const x of taskGoalMoves(g, ctxNow, now)) add(x.move, replaceTasks(ctxNow, x.changed)); continue; }
    const [kind, ...rest] = g.key.split(':');
    const ref = rest.join(':');
    const short = g.expected != null && g.value != null ? g.expected - g.value : null;
    if (g.kind === 'count' && g.direction === 'up' && short > 0) {
      if (g.key === 'tasksDone') {
        const k = Math.ceil(short - 1e-9);
        if (k >= 2 && open.length >= 2) {
          const pick = open.slice(0, Math.min(k, open.length));
          add({ id: `finishN:${g.id}`, kind: 'finishN', title: `Finish ${pick.length} more tasks`, why: listNames(pick.map((t) => t.title), 2), action: { kind: 'open', taskId: pick[0].id } },
            replaceTasks(ctxNow, pick.map((t) => finished(t, now))));
        }
      } else if (kind === 'tag') {
        const slug = canonTag(ref);
        const tagged = (data.tasks || []).filter((t) => isOpen(t) && hasTag(t, slug)).sort((a, b) => (num(a.createdAt) || 0) - (num(b.createdAt) || 0));
        const k = Math.min(Math.ceil(short - 1e-9), tagged.length);
        if (k > 0) {
          add({ id: `tag:${g.id}`, kind: 'finishN', title: k === 1 ? `Finish ${tagged[0].title}` : `Finish ${k} #${slug} tasks`, why: k === 1 ? `#${slug} · ${g.label} is behind pace` : listNames(tagged.slice(0, k).map((t) => t.title), 2), action: { kind: 'open', taskId: tagged[0].id } },
            replaceTasks(ctxNow, tagged.slice(0, k).map((t) => closed(t, now))));
        }
      } else if (g.key === 'hoursWorked' && lead) {
        const endOfDay = new Date(now); endOfDay.setHours(20, 0, 0, 0);
        const ms = Math.min(Math.ceil((short * H) / (15 * 60000)) * 15 * 60000, Math.max(15 * 60000, endOfDay.getTime() - now), 10 * H);
        add({ id: `hours:${g.id}`, kind: 'hours', title: `Put ${hrs(ms)} more on ${dept.name} today`, why: `${g.label} is ${hrs(short * H)} behind pace — ${lead.id === activeTaskId ? 'keep going on' : 'start'} ${lead.title}`, action: { kind: lead.id === activeTaskId ? 'open' : 'start', taskId: lead.id } },
          replaceTasks(ctxNow, [withHours(lead, ms, now)]));
      } else if (g.key === 'photosTaken' && lead) {
        const k = Math.ceil(short - 1e-9);
        add({ id: `photos:${g.id}`, kind: 'photos', title: `Log ${plural(k, 'photo')}`, why: `on ${lead.title} — ${g.label} is behind pace`, action: { kind: 'camera', taskId: lead.id } },
          replaceTasks(ctxNow, [withPhotos(lead, k, now)]));
      } else if ((kind === 'metric' || kind === 'personal') && g.tappable) {
        const k = Math.ceil(short - 1e-9);
        const values = kind === 'personal' ? 'personalMetricValues' : 'metricValues';
        add({ id: `log:${g.id}`, kind: 'log', title: `Log ${k} more · ${g.label}`, why: `${formatGoalValue(g.value, g.unit)} so far, ${formatGoalValue(Math.round(g.expected * 10) / 10, g.unit)} by now`, action: { kind: 'log', metricId: ref, personal: kind === 'personal' } },
          { ...ctxNow, [values]: [...(data[values] || []), { id: 'sim-log', metricId: ref, value: k, date: localDateStr(now), at: now }] });
      }
    } else if (g.kind === 'level' && g.direction === 'down' && g.value > g.target) {
      if (kind === 'open') {
        const slug = canonTag(ref);
        const tagged = (data.tasks || []).filter((t) => isOpen(t) && hasTag(t, slug)).sort((a, b) => (num(a.createdAt) || 0) - (num(b.createdAt) || 0));
        const k = Math.min(Math.ceil(g.value - g.target - 1e-9), tagged.length);
        if (k > 0) {
          const verb = slug === 'deploy' ? 'Deploy' : 'Close';
          add({ id: `close:${g.id}`, kind: 'close', title: k === 1 ? `${verb} ${tagged[0].title}` : `${verb} ${k} ${slug === 'deploy' ? 'builds waiting' : `open #${slug}`}`, why: `${g.label}: ${g.value} against a limit of ${g.target}${k > 1 ? ` — oldest ${tagged[0].title}` : ''}`, action: { kind: 'open', taskId: tagged[0].id } },
            replaceTasks(ctxNow, tagged.slice(0, k).map((t) => closed(t, now))));
        }
      } else if (g.key === 'ops:openTasks' || g.key === 'ops:oldestOpenDays') {
        const oldest = [...open].sort((a, b) => (num(a.createdAt) ?? Infinity) - (num(b.createdAt) ?? Infinity));
        const k = g.key === 'ops:openTasks' ? Math.min(Math.ceil(g.value - g.target), oldest.length) : Math.min(1, oldest.length);
        if (k > 0) {
          const age = num(oldest[0].createdAt) ? Math.floor((now - num(oldest[0].createdAt)) / 86400000) : null;
          add({ id: `close:${g.id}`, kind: 'close', title: k === 1 ? `Finish or drop ${oldest[0].title}` : `Finish or drop the ${k} oldest tasks`, why: `${g.label}: ${formatGoalValue(g.value, g.unit)}, limit ${formatGoalValue(g.target, g.unit)}${age != null ? ` — the oldest is ${age} d` : ''}`, action: { kind: 'open', taskId: oldest[0].id } },
            replaceTasks(ctxNow, oldest.slice(0, k).map((t) => closed(t, now))));
        }
      } else if (g.key === 'ops:receiptsNoJob') {
        const loose = looseReceiptCount(data.receipts);
        const k = Math.ceil(g.value - g.target);
        if (k > 0 && loose) add({ id: `receipts:${g.id}`, kind: 'receipts', title: `Put ${plural(k, 'receipt')} on a job`, why: `${g.label}: ${g.value}, limit ${g.target}`, action: { kind: 'goal', goalId: g.id } }, { ...ctxNow, receipts: dropLoose(data.receipts, k) });
      }
    } else if (g.key === 'ops:booksCurrentPct') {
      const loose = looseReceiptCount(data.receipts);
      if (loose) add({ id: `receipts:${g.id}`, kind: 'receipts', title: `Put ${plural(loose, 'receipt')} on a job`, why: `${g.label}: 20 points off each receipt waiting`, action: { kind: 'goal', goalId: g.id } }, { ...ctxNow, receipts: dropLoose(data.receipts, loose) });
    }
  }

  // one line per move; at most two single-task finishes (the rest of the list is the same move again)
  const seen = new Set(); let singles = 0;
  return moves.sort((a, b) => b.exactGain - a.exactGain || a.seq - b.seq)   // ties keep the work order (running, overdue, today…)
    .filter((m) => { if (seen.has(m.title)) return false; seen.add(m.title); if (m.kind === 'finish' && ++singles > 2) return false; return true; })
    .slice(0, limit);
}

/** Receipts with the first k loose ones filed (the receipts move, re-scored). */
function dropLoose(receipts = [], k) {
  let left = k;
  return (receipts || []).map((r) => {
    if (left <= 0 || looseReceiptCount([r]) === 0) return r;
    left -= 1;
    return { ...r, status: 'void' };
  });
}

// ── data health ─────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * What limits the score's accuracy here (the goals' "left out" rows, gathered across goals, each with what fixes it).
 * Only the checks that touch this department's goals — or, with no goals yet, its presets / suggestions. `from`..`to` =
 * the window (default this month). → [{ id, icon, title, detail, goals: [labels], items: [{ title, taskId?, projectId?, goalId? }], fix }]
 * fix: 'task' (open the task) · 'project' (project settings) · 'goal' (edit the goal) · 'rate' (your BattleBooks rate) · null
 */
export function dataHealth(dept, data = {}, view = null, { now = data.now || Date.now(), from = null, to = null } = {}) {
  if (!dept) return [];
  const today = localDateStr(now);
  const win = { from: from || goalWindow('month', now).from, to: to || today };
  const inWin = (d) => !!d && d >= win.from && d <= win.to;
  const goals = view?.goals?.length ? view.goals : [];
  const keys = new Set(goals.length ? goals.map((g) => g.key) : (fixedPresets(dept) || suggestedGoals(dept)).map((g) => g.key));
  const labelOf = (k) => goals.find((g) => g.key === k)?.label || goalMetric(k)?.label || k;
  const using = (...ks) => ks.filter((k) => keys.has(k)).map(labelOf);
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const mine = (t) => wd.forTask(t)?.id === dept.id;
  const parents = new Set(tasks.filter((t) => t.parentId && t.estimatedMinutes > 0).map((t) => t.parentId));
  const out = [];
  const push = (x) => { if (x.items.length) out.push(x); };
  const taskRow = (t, extra = '') => ({ title: `${t.title}${extra}`, taskId: t.id });

  const doneHere = tasks.filter((t) => !t.deletedAt && inWin(doneDate(t)) && mine(t) && !isOverheadTask(t) && !parents.has(t.id));
  const estGoals = using('onEstimate', 'earnedPerHour');
  if (estGoals.length) {
    push({ id: 'noEstimate', icon: 'clock', title: `${plural(doneHere.filter((t) => !(t.estimatedMinutes > 0) && !t.runId).length, 'finished task')} with no estimate`, detail: `${estGoals.join(' and ')} leave${estGoals.length === 1 ? 's' : ''} them out — add the estimate`, goals: estGoals, fix: 'task',
      items: doneHere.filter((t) => !(t.estimatedMinutes > 0) && !t.runId).map((t) => taskRow(t)) });
  }
  if (keys.has('onEstimate')) {
    const noTime = doneHere.filter((t) => t.estimatedMinutes > 0 && !t.runId && !((t.timeSpent || 0) >= 60000));
    push({ id: 'noTime', icon: 'activity', title: `${plural(noTime.length, 'finished task')} with no time tracked`, detail: `${labelOf('onEstimate')} leaves them out — log the time on the task`, goals: using('onEstimate'), fix: 'task', items: noTime.map((t) => taskRow(t, ` · ${hrs(t.estimatedMinutes * 60000)} est`)) });
  }
  if (keys.has('earnedPerHour') || keys.has('jobMargin')) {
    const jobs = new Map((data.jobs || []).map((j) => [j.id, j]));
    // the jobs Earned per hour can't price — read off the one count (jobpay.js earnedByJobType), so this list and the
    // number can't disagree: fixed / product with hours and no labour in the price (no bid labour, no contract), T&M
    // with hours and no bill rate (project, role rates or department); a job with no type and neither = no value
    const E = earnedByJobType({ tasks, projects, departments, jobs: data.jobs || [], policy: data.overhead?.policy || data.policy || null,
      from: win.from, to: win.to, entryIn: (e, t) => !!t.projectId && wd.forEntry(e, t)?.id === dept.id, taskIn: mine });
    const noValue = []; const tmNoRate = [];
    for (const [pid, ms] of E.msByProject) {
      const j = E.jobInfo(pid);
      if (!j.p) continue;
      const unrated = j.kind === 'tm' && E.tmNoRate.some((x) => x.pid === pid);
      if (unrated && j.type) tmNoRate.push({ title: `${j.p.name || 'A job'} · ${hrs(ms)} unbilled`, projectId: pid });
      else if (unrated || (j.kind === 'fixed' && !(j.labour?.labour > 0))) noValue.push({ title: `${j.p.name || 'A job'} · ${hrs(ms)} worked`, projectId: pid });
    }
    if (keys.has('earnedPerHour')) {
      push({ id: 'noValue', icon: 'dollar-sign', title: `${plural(noValue.length, 'job')} with no contract value or bill rate`, detail: `${labelOf('earnedPerHour')} counts their hours but earns nothing — set the contract or the rate`, goals: using('earnedPerHour'), fix: 'project', items: noValue });
      push({ id: 'tmNoRate', icon: 'dollar-sign', title: `${plural(tmNoRate.length, 'T&M job')} with no rate`, detail: `${labelOf('earnedPerHour')} counts the hours at $0 — set the project's bill rate`, goals: using('earnedPerHour'), fix: 'project', items: tmNoRate });
    }
    if (keys.has('jobMargin')) {
      const doneJobs = projects.filter((p) => p && projectStatus(p) === 'completed' && num(p.completedAt) && localDateStr(num(p.completedAt)) >= addDaysStr(today, -90) && departmentOf(p, departments)?.id === dept.id
        && !(contractValueOf(p, p.bookProjectId ? jobs.get(p.bookProjectId) : null) > 0));
      push({ id: 'jobNoValue', icon: 'briefcase', title: `${plural(doneJobs.length, 'finished job')} with no contract value`, detail: `${labelOf('jobMargin')} leaves them out — add what the job was worth`, goals: using('jobMargin'), fix: 'project', items: doneJobs.map((p) => ({ title: p.name || 'A job', projectId: p.id })) });
    }
  }
  if (keys.has('photosTaken')) {
    const undated = []; const byAdded = [];
    for (const t of tasks) {
      if (t.deletedAt || !mine(t)) continue;
      const ps = taskPhotoList(t);
      const none = ps.filter((p) => !(p.at > 0)).length;
      const added = ps.filter((p) => p.at > 0 && !p.takenAt && inWin(localDateStr(p.at))).length;
      if (none) undated.push(taskRow(t, ` · ${plural(none, 'photo')}`));
      if (added) byAdded.push(taskRow(t, ` · ${plural(added, 'photo')}`));
    }
    push({ id: 'photoNoTime', icon: 'camera', title: `${plural(undated.length, 'task')} with photos that have no time`, detail: `${labelOf('photosTaken')} leaves them out — older photos kept no time`, goals: using('photosTaken'), fix: 'task', items: undated });
    push({ id: 'photoAdded', icon: 'image', title: `${plural(byAdded.length, 'task')} with photos counted on the day added`, detail: 'they had no taken time, so they count when they were added', goals: using('photosTaken'), fix: 'task', items: byAdded });
  }
  const upTag = (k) => k.startsWith('tag:') && (goals.find((g) => g.key === k)?.direction || goalMetric(k)?.direction) === 'up';   // a limit (#callback) isn't missing work
  const goalTags = [...keys].filter(upTag).map((k) => canonTag(k.slice(4)));
  const tagKeys = goalTags.length ? goalTags : deptRole(dept) === 'dev' ? ['shipped', 'bug', 'improvement'] : [];
  if (tagKeys.length) {
    const untagged = tasks.filter((t) => !t.deletedAt && inWin(doneDate(t)) && mine(t) && !t.bucket && !t.quickTaskId && !t.runId && !t.parentId
      && !(Array.isArray(t.tags) && t.tags.some((x) => tagKeys.includes(canonTag(String(x))))));
    const tagGoals = [...keys].filter(upTag).map(labelOf);
    push({ id: 'untagged', icon: 'tag', title: `${plural(untagged.length, 'finished task')} with no goal tag`, detail: tagGoals.length ? `not in ${tagGoals.join(', ')} — tag ${tagKeys.map((x) => `#${x}`).join(' / ')} if it was one` : `tag ${tagKeys.map((x) => `#${x}`).join(', ')} so a goal can count it`, goals: tagGoals, fix: 'task', items: untagged.map((t) => taskRow(t)) });
  }
  if (keys.has('ops:receiptsNoJob') || keys.has('ops:booksCurrentPct') || deptRole(dept) === 'field' || deptRole(dept) === 'finance') {
    const loose = (data.receipts || []).filter((r) => looseReceiptCount([r]) > 0);
    push({ id: 'receipts', icon: 'file-text', title: `${plural(loose.length, 'receipt')} on no job`, detail: 'job costs and Books current miss them — put each line on a job', goals: using('ops:receiptsNoJob', 'ops:booksCurrentPct'), fix: null,
      items: loose.map((r) => { const pg = Object.values(r.pages || {})[0] || {}; return { title: `${pg.vendorName || r.vendorName || 'A receipt'}${num(r.total) ? ` · $${num(r.total).toFixed(2)}` : ''}` }; }) });
  }
  for (const g of goals) {
    if (g.status === 'unset') push({ id: `unset:${g.id}`, icon: 'target', title: `${g.label} has no target`, detail: g.needsRate ? 'it follows your BattleBooks rate, which isn’t set' : 'it isn’t in the score until it has one', goals: [g.label], fix: g.needsRate ? 'rate' : 'goal', items: [{ title: g.needsRate ? 'Set your rate in BattleBooks' : `Set a target for ${g.label}`, goalId: g.id }] });
    else if (g.status === 'feed') push({ id: `feed:${g.id}`, icon: 'cloud-off', title: `${g.label} is waiting on ${g.feed === 'sales' ? 'the Bids feed' : 'the books'}`, detail: 'it turns on when the numbers reach this device', goals: [g.label], fix: null, items: [{ title: STATUS_LABEL.feed, goalId: g.id }] });
  }
  return out;
}
